import React, { useContext, useMemo } from 'react';
import { Row } from "tea-component";
import { shallowEqual, useSelector } from 'umi';
import CountryRegionRowItem from '@/pages/dashboard/components/CountryRegionRowItem';
import useData from './useData';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import usePlatformData from './usePlatformData';
import { formatterPlatformList, jumpDetail } from '@/pages/dashboard/utils/utils';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { CountriesContextsProps } from '../_type';
import { dauMetricsArr, shareAndTypeObj, titleAndTypeObj } from './_costant';
import { useMetricsConfig } from './useMetricsConfig';

import './index.scss';

const CountriesContexts = (props: CountriesContextsProps) => {
  const { regionDataType = 'os', config, data: configData, tabMsg } = props;
  const timeZone = parseInt((configData)?.time_zone || '', 10) || 0;
  const defaultComConfig = {
    isHaveBar: true,
    isHaveLine: true,
    isHavePie: true,
    isHaveMap: regionDataType === 'country',
    isHaveEyes: true,
    isHaveShare: true,
    isHaveStackLine: false
  };
  const { regionDataType: tabMetric, defaultRegionDataType, optionsInit, metricsInfo } = (configData) || {};
  const { 
    metric: configMetric, 
    api, 
    haveCalBubble, 
    isUserQuota, 
    showConfig, 
    comConfig: externalComConfig, 
    chartDataKey, 
    tableDataKey, 
    jumpUrl, 
    extraColumn, 
    haveLastDate,
    // 新增配置项（可选）
    metricEnName,
    metricCnName,
    formatRule,
    barChartLabelFormatRule,
    changeFormatRule,
    shareFormatRule,
    titleNameMsg,
    metricRangeEnName,
    metricRangeCnName,
    defaultChartType, // 新增：默认显示的图表类型
    extraParams
  } = config || {};
  
  // 合并 comConfig，外部配置会覆盖默认配置
  const comConfig = {
    ...defaultComConfig,
    ...externalComConfig,
  };
  
  const metric = defaultRegionDataType ? tabMetric : configMetric;
  const contextObj = useContext(FilterContext);
  const { filterVal, filterLoading } = contextObj || {};
  const { timeType = 'Day', rangeTime } = filterVal || {};

  const { gameCode, selector } = useSelector((state: any) => ({
    gameCode: state.dashboard?.currentGame?.game_code,
    selector: state?.dashboardSetting?.selector,
  }), shallowEqual);

  const platformList = useMemo(() => {
    const osList = selector?.os_list?.filter((item: any) => item?.os_id !== '255');
    return osList;
  }, [selector]);

  const platformByDataAllOs: any = usePlatformData(
    filterVal,
    filterLoading,
    api,
    haveCalBubble,
    PERIODTIMEOBJ[timeType],
    regionDataType,
    metric,
    haveLastDate,
    timeZone,
    extraParams
  );

  const progressList = formatterPlatformList(platformList, platformByDataAllOs.componentData.dwm_list);

  const jumpToLink = useMemo(() => () => {
    if (jumpUrl && jumpUrl !== "none") {
      const formValue = {
        filterValue: filterVal,
        timeType,
        diff: filterVal?.diff
      };
      jumpDetail(`/dashboard/game/${gameCode}${jumpUrl}`, formValue);
    }
  }, [jumpUrl, gameCode, filterVal, timeType]);

  const compData = useData(
    filterVal,
    filterLoading,
    api,
    PERIODTIMEOBJ[timeType],
    regionDataType,
    metric,
    chartDataKey,
    tableDataKey,
    haveLastDate,
    timeZone,
    extraParams
  );
  const { data, loading, error, isSingle } = compData || {};

  // 计算默认图表索引
  const calculateInitActiveIndex = useMemo(() => {
    // 如果有配置 defaultChartType，则根据配置计算索引
    if (defaultChartType) {
      const chartTypeMap: Record<string, number> = {
        'bar': 0,
        'line': comConfig.isHaveBar ? 1 : 0,
        'stackLine': (comConfig.isHaveBar ? 1 : 0) + (comConfig.isHaveLine ? 1 : 0),
        'pie': (comConfig.isHaveBar ? 1 : 0) + (comConfig.isHaveLine ? 1 : 0) + (comConfig.isHaveStackLine ? 1 : 0),
        'map': (comConfig.isHaveBar ? 1 : 0) + (comConfig.isHaveLine ? 1 : 0) + (comConfig.isHaveStackLine ? 1 : 0) + (comConfig.isHavePie ? 1 : 0),
      };
      return chartTypeMap[defaultChartType] || 0;
    }
    
    // 默认逻辑：如果是 country 且是 dau 相关指标，默认显示地图（索引3）
    if (regionDataType === 'country' && dauMetricsArr?.includes(metric)) {
      return 3;
    }
    
    return 0;
  }, [defaultChartType, regionDataType, metric, comConfig]);

  // 使用新的 hook 处理指标配置
  const { tableColumns, chartTooltipsConfig, titleContent } = useMetricsConfig({
    metricEnName,
    metricCnName,
    metricRangeEnName,
    metricRangeCnName,
    formatRule,
    changeFormatRule,
    shareFormatRule,
    metricsInfo,
    regionDataType,
    metric,
    isUserQuota,
    comConfig,
    jumpUrl,
    gameCode,
    haveCalBubble,
    progressList,
    platformByDataAllOs,
    defaultRegionDataType,
    optionsInit,
    jumpToLink,
    tabMsg,
    titleNameMsg,
    isSingle,
  });
  const initActiveIndex = calculateInitActiveIndex;

  return (
    <Row gap={24} className="dbd-breakdown-box">
      {
        <CountryRegionRowItem
          prefix={dauMetricsArr?.includes(metric) ? 'Avg' : 'Sum'}
          titleContent={titleContent}
          comConfig={comConfig}
          initActiveIndex={initActiveIndex}
          titleAndType={titleAndTypeObj?.[metric]}
          regionDataType={regionDataType}
          timeRange={rangeTime}
          loading={loading}
          isSingle={isSingle}
          isError={error}
          data={data}
          isUserQuota={isUserQuota}
          timeType={timeType}
          gameCode={gameCode}
          morePie
          isAllUpper={true}
          showConfig={showConfig}
          shareAndType={shareAndTypeObj?.[metric]}
          chartTooltipsConfig={chartTooltipsConfig}
          tabOption={tabMetric}
          extraColumn={extraColumn}
          tableColumns={tableColumns}
          barChartLabelFormatRule={barChartLabelFormatRule}
        />
      }
    </Row>
  );
};

export default CountriesContexts;